import { useEffect, useState, useRef, useCallback } from "react";
import useFleetStore from "../utils/store";
import { fetchAlertsForFleet } from "../lib/api";
import { useFleetStatusCheck } from "../hooks/useFleetStatusCheck";
import AlarmCard from "../components/AlarmCard";
import LoadingModal from "../components/Common/LoadingModal";
import AlarmFilter from "../components/Common/AlarmsFilterDropdown";
import AlarmList from "../components/AlarmList";
import GridListViewComponent from "../components/Common/GridListViewComponent";
import { useLocation, useNavigate } from "react-router-dom";
import gotoUnits from "../assets/images/gotoUnits.png";
import gotoUnitsInverse from "../assets/images/gotoUnitsInverse.png";
import Pagination from "../components/Common/Pagination";

import SearchComponent from "../components/Common/SearchComponent";
import { SearchContext } from "../lib/analyticsEvents";
import { removeQueryParam } from "../utils/queryParamNavigation";
import { unitListReturnState } from "../utils/unitListReturn";

const DEFAULT_PAGE_SIZE = 25;
const GRID_VIEW_STORAGE_KEY = "alarmsViewMode";
const RESOLVED_ALERTS_QUERY_PARAM = "resolvedAlerts";
const Alerts = () => {
  const { selectedFleet } = useFleetStore();
  const getStoredGridView = () => {
    if (window.innerWidth < 1025) return true;
    try {
      const stored = localStorage.getItem(GRID_VIEW_STORAGE_KEY);
      return stored ? stored === "grid" : true;
    } catch (error) {
      return true;
    }
  };
  const [isfetchingAlarms, setIsFetchingAlarms] = useState(false);
  const [alarmsdata, setAlarmsData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("active");
  const [gridView, setGridView] = useState(() => getStoredGridView());
  const loaderRef = useRef(null);
  const resolvedAlertsSelectionAppliedRef = useRef(false);
  const resolvedAlertsQueryRemovedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pageNumber = 1;
  const [searchAlarmText, setSearchAlarmText] = useState("");

  const selectedOptionRef = useRef(selectedOption);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount , setTotalCount] = useState(0);

  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldLoadResolvedAlerts = searchParams.get(RESOLVED_ALERTS_QUERY_PARAM) === "true";

    if (!shouldLoadResolvedAlerts) {
      return;
    }

    if (!resolvedAlertsSelectionAppliedRef.current) {
      resolvedAlertsSelectionAppliedRef.current = true;
      setSelectedOption("resolved");
      setCurrentPage(0);
    }

    if (resolvedAlertsQueryRemovedRef.current) {
      return;
    }

    resolvedAlertsQueryRemovedRef.current = true;
    removeQueryParam(navigate, RESOLVED_ALERTS_QUERY_PARAM);
  }, [location.search, navigate]);

  const fleetAlerts = useCallback(
    async (showLoader = true, replace = true) => {
      const payload = {
        pageNumber: currentPage + 1,
        pageSize: DEFAULT_PAGE_SIZE,
        unid: selectedFleet?.unid,
        status: selectedOptionRef.current,
        search: searchAlarmText
      };

      if (showLoader) setIsFetchingAlarms(true);

      const response = await fetchAlertsForFleet(payload);
      const newAlarms = response?.data?.alerts || [];
      setTotalCount(response?.data?.count || 0)
      setAlarmsData(newAlarms);

      if (showLoader) setIsFetchingAlarms(false);
    },
    [selectedFleet?.unid, currentPage, searchAlarmText]
  );

  useEffect(() => {
    if (selectedFleet?.unid) {
      setAlarmsData([]);
      fleetAlerts(true, true);
    }
  }, [selectedFleet?.unid, selectedOption, fleetAlerts]);

  useFleetStatusCheck({
    unid: selectedFleet?.unid,
    isActive: location.pathname === "/alerts",
    onComplete: () => fleetAlerts(false, true),
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1025) {
        setGridView(true);
        return;
      }
      setGridView(getStoredGridView());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1025) return;
    try {
      localStorage.setItem(GRID_VIEW_STORAGE_KEY, gridView ? "grid" : "list");
    } catch (error) {
      // Ignore localStorage write failures (e.g. privacy mode).
    }
  }, [gridView]);

  const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

  const handlePageClick = ({selected}) => {
    setCurrentPage(selected)
  }

  return (<>
    <div className="titleAndFilters" data-testid="alerts-title-and-filters">
      <div className="flex  gap-3  justify-between items-center  flex-wrap">
        <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="alerts-title">
          Alerts {totalCount > 0 && (
          <span data-testid="alerts-title-count">({totalCount})</span> )}
        </h2>
        <div className="searchAndFilter flex items-center gap-4 max-[430px]:flex-wrap flex-wrap grow justify-end">
          <SearchComponent
            searchUnitText={searchAlarmText}
            setSearchUnitText={(val) => {
              setSearchAlarmText(val);
              setCurrentPage(0);
            }}
            trackingContext={SearchContext.alerts}
          />

          <AlarmFilter
            selectedOption={selectedOption}
            setSelectedOption={(val) => {
              setSelectedOption(val);
              setCurrentPage(0);
            }}
            data-testid="alerts-status-filter"
          />

          <div className="hidden lg:flex items-center gap-4 max-[430px]:w-full max-[430px]: justify-end">
            <GridListViewComponent gridView={gridView} setGridView={setGridView} data-testid="alerts-grid-list-view-toggle" />
          </div>
        </div>
      </div>
    </div>
    {gridView ? (
      <div className={`mt-4 unitsHolder ${alarmsdata?.length === 0 ? "flex grow items-center justify-center" : ""}`}>
        {pageNumber === 1 && isfetchingAlarms ? (
          <div className="w-full flex justify-center py-4">
            <div className="bg-white h-screen">
              <LoadingModal />
            </div>
          </div>
        ) : (
          alarmsdata?.length > 0 && (
            <div className="grid max-[430px]:grid-cols-1 grid-cols-[repeat(auto-fill,minmax(312px,1fr))] gap-6">
              {alarmsdata.map((alarm, index) => (
                <AlarmCard
                  key={alarm?.id || alarm?.serialNumber}
                  alarm={alarm}
                  index={index}
                  timeZone={selectedFleet?.timeZone}
                  navigationState={unitListReturnState("alerts", location.search)}
                />
              ))}

              {isfetchingAlarms && pageNumber > 1 && (
                <div className="w-full col-span-full flex justify-center py-4 text-dark-grey text-sm">
                  Loading more alerts...
                </div>
              )}

              <div ref={loaderRef} className="h-1 col-span-full"></div>
            </div>
          )
        )}

        {!isfetchingAlarms && alarmsdata?.length === 0 && (
          <div className="w-full sm:w-3/4 grow flex flex-col justify-center md:w-96 mx-auto h-full" data-testid="alerts-grid-empty-state">
            <div className="w-44 h-40 mx-auto">
              <img
                src={gotoUnits}
                alt="No Units"
                className="size-full h-auto mix-blend-multiply dark:hidden block"
            />
            <img
                src={gotoUnitsInverse}
                alt="No Units Inverse"
                className="size-full h-auto hidden dark:block"
            />
            </div>
            <div>
              <h3 className="subtitle text-center text-blue font-semibold tracking-[0.036px] text-xl leading-5 lg:text-2xl lg:leading-7 dark:text-white mt-4">
                No Alerts
              </h3>
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className={`mt-4 unitsListHolder overflow-auto rounded-md ${alarmsdata?.length === 0 ? "flex grow items-center justify-center" : ""}`}>
        {pageNumber === 1 && isfetchingAlarms && (
          <div className="bg-white h-screen">
            <LoadingModal />
          </div>
        )}

        {alarmsdata?.length > 0 && (
          <>
            <AlarmList
              alarm={alarmsdata}
              timeZone={selectedFleet?.timeZone}
              navigationState={unitListReturnState("alerts", location.search)}
            />
            {isfetchingAlarms && pageNumber > 1 && (
              <div className="flex justify-center py-3">
                <span className="text-dark-grey text-sm">Loading more alerts...</span>
              </div>
            )}
            <div ref={loaderRef} className="h-1 w-full" />
          </>
        )}

        {!isfetchingAlarms && alarmsdata?.length === 0 && (
          <div className="w-full sm:w-3/4 grow flex flex-col justify-center md:w-96 mx-auto h-full" data-testid="alerts-list-empty-state">
            <div className="w-44 h-40 mx-auto">
              <img
                src={gotoUnits}
                alt="go to units"
                className="size-full h-auto mix-blend-multiply dark:hidden block"
              />
              <img
                src={gotoUnitsInverse}
                alt="No Units Inverse"
                className="size-full h-auto hidden dark:block"
            />
            </div>
            <div>
              <h3 className="subtitle text-center text-blue font-semibold tracking-[0.036px] text-xl leading-5 lg:text-2xl lg:leading-6 mt-4 dark:text-white">
                No Alerts
              </h3>
            </div>
          </div>
        )}
      </div>
    )}

      {totalCount > DEFAULT_PAGE_SIZE && (
        <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage}/>
      )}


  </>)
}

export default Alerts;
